import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, PencilSimple, CaretDown, CaretRight, ArrowUp, ArrowDown,
  FilmSlate, X, Copy, WarningCircle, Check, LinkSimple,
} from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Button } from "../components/ui/button";

// Admin-only manager for the official CATEGORY → SUBCATEGORY → CREATIVE DIRECTOR
// library. Every mutation is re-checked server-side; this page is just the panel.

const errMsg = (e, fallback) => {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : fallback;
};

const STATUSES = ["draft", "active", "inactive", "archived"];
const STATUS_STYLE = {
  draft: "bg-blue-500/20 text-blue-300",
  active: "bg-emerald-500/20 text-emerald-300",
  inactive: "bg-zinc-600/30 text-zinc-400",
  archived: "bg-red-500/15 text-red-300",
};

const inputCls = "w-full rounded-lg bg-black/40 border border-white/10 px-3 py-2.5 text-sm outline-none focus:border-amber-400/60";

function StatusSelect({ value, onChange, testid }) {
  return (
    <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
      className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-1 border-0 outline-none cursor-pointer ${STATUS_STYLE[value] || STATUS_STYLE.active}`}>
      {STATUSES.map((s) => <option key={s} value={s} className="bg-[#161616] text-white">{s}</option>)}
    </select>
  );
}

function EditDialog({ edit, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [icon, setIcon] = useState("");
  const [keywords, setKeywords] = useState("");
  useEffect(() => {
    if (!edit) return;
    setName(edit.doc?.name || "");
    setDescription(edit.doc?.description || "");
    setIcon(edit.doc?.icon || "");
    setKeywords((edit.doc?.keywords || []).join(", "));
  }, [edit]);
  if (!edit) return null;
  const isCat = edit.kind.includes("category") && !edit.kind.includes("sub");
  const save = async () => {
    if (!name.trim()) { toast.error("Name is required."); return; }
    const payload = {
      name: name.trim(), description: description.trim(),
      keywords: keywords.split(",").map((k) => k.trim()).filter(Boolean),
      ...(isCat ? { icon: icon.trim() } : {}),
      ...(edit.kind === "new-subcategory" ? { category_id: edit.categoryId } : {}),
    };
    try {
      if (edit.kind === "new-category") await api.post("/admin/library-pro/categories", payload);
      else if (edit.kind === "edit-category") await api.put(`/admin/library-pro/categories/${edit.doc.id}`, payload);
      else if (edit.kind === "new-subcategory") await api.post("/admin/library-pro/subcategories", payload);
      else if (edit.kind === "edit-subcategory") await api.put(`/admin/library-pro/subcategories/${edit.doc.id}`, payload);
      toast.success("Saved.");
      onSaved();
      onClose();
    } catch (e) { toast.error(errMsg(e, "Couldn't save.")); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0d0d0d] border-white/10 text-white max-w-md" data-testid="libadmin-edit-dialog">
        <DialogHeader><DialogTitle className="font-display text-lg">
          {{ "new-category": "New category", "edit-category": "Edit category", "new-subcategory": "New subcategory", "edit-subcategory": "Edit subcategory" }[edit.kind]}
        </DialogTitle></DialogHeader>
        <div className="space-y-3">
          <input data-testid="libadmin-edit-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" className={inputCls} />
          {isCat && <input data-testid="libadmin-edit-icon" value={icon} onChange={(e) => setIcon(e.target.value)} placeholder="Icon (emoji)" className={inputCls} />}
          <textarea data-testid="libadmin-edit-desc" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Public description" rows={2} className={inputCls} />
          <input data-testid="libadmin-edit-keywords" value={keywords} onChange={(e) => setKeywords(e.target.value)} placeholder="Search keywords, comma separated" className={inputCls} />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-zinc-400 hover:text-white hover:bg-white/10">Cancel</Button>
          <Button data-testid="libadmin-edit-save" onClick={save} className="bg-amber-400 text-black hover:bg-amber-300 font-bold">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DirectorDialog({ dialog, onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", short_description: "", category: "", keywords: "", hidden_prompt: "", is_active: true });
  const [loadingPrompt, setLoadingPrompt] = useState(false);
  useEffect(() => {
    if (!dialog) return;
    if (dialog.kind === "edit-director") {
      setLoadingPrompt(true);
      api.get("/admin/creative-directors").then((r) => {
        const full = (r.data || []).find((d) => d.id === dialog.doc.id);
        setForm({
          name: full?.name || "", short_description: full?.short_description || "",
          category: full?.category || "", keywords: (full?.keywords || []).join(", "),
          hidden_prompt: full?.hidden_prompt || "", is_active: full?.is_active !== false,
        });
      }).catch(() => toast.error("Couldn't load the director.")).finally(() => setLoadingPrompt(false));
    } else {
      setForm({ name: "", short_description: "", category: "", keywords: "", hidden_prompt: "", is_active: true });
    }
  }, [dialog]);
  if (!dialog) return null;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required."); return; }
    const payload = {
      name: form.name.trim(), short_description: form.short_description.trim(),
      category: form.category.trim(), hidden_prompt: form.hidden_prompt,
      keywords: form.keywords.split(",").map((k) => k.trim()).filter(Boolean),
      is_active: form.is_active,
    };
    try {
      if (dialog.kind === "edit-director") await api.put(`/admin/creative-directors/${dialog.doc.id}`, payload);
      else await api.post("/admin/creative-directors", { ...payload, library_only: true });
      toast.success("Director saved.");
      onSaved();
      onClose();
    } catch (e) { toast.error(errMsg(e, "Couldn't save the director.")); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0d0d0d] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="libadmin-director-dialog">
        <DialogHeader><DialogTitle className="font-display text-lg">
          {dialog.kind === "edit-director" ? `Edit — ${dialog.doc.name} (v${dialog.doc.version || 1})` : "New Creative Director"}
        </DialogTitle></DialogHeader>
        {loadingPrompt ? <p className="text-sm text-zinc-500 py-8 text-center">Loading…</p> : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="libadmin-dir-name" value={form.name} onChange={set("name")} placeholder="Public display name" className={inputCls} />
              <input data-testid="libadmin-dir-category" value={form.category} onChange={set("category")} placeholder="Category label (chip)" className={inputCls} />
            </div>
            <input data-testid="libadmin-dir-desc" value={form.short_description} onChange={set("short_description")} placeholder="Public description" className={inputCls} />
            <input data-testid="libadmin-dir-keywords" value={form.keywords} onChange={set("keywords")} placeholder="Search keywords, comma separated" className={inputCls} />
            <div>
              <label className="text-xs uppercase tracking-wider text-amber-300/80 font-bold mb-1.5 block">Protected master prompt <span className="text-zinc-600 normal-case font-normal">— server-side only, never shown to users</span></label>
              <textarea data-testid="libadmin-dir-prompt" value={form.hidden_prompt} onChange={set("hidden_prompt")} rows={8} className={inputCls} />
              {dialog.kind === "edit-director" && <p className="text-[10px] text-zinc-500 mt-1">Changing the prompt archives the previous version and bumps the version number.</p>}
            </div>
            <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
              <input type="checkbox" checked={form.is_active} onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))} /> Active (visible in the library)
            </label>
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-zinc-400 hover:text-white hover:bg-white/10">Cancel</Button>
          <Button data-testid="libadmin-dir-save" onClick={save} className="bg-amber-400 text-black hover:bg-amber-300 font-bold">Save director</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AssignDialog({ sub, allDirectors, onClose, onSaved }) {
  const [q, setQ] = useState("");
  if (!sub) return null;
  const assignedIds = new Set((sub.directors || []).map((d) => d.id));
  const candidates = allDirectors.filter((d) =>
    !assignedIds.has(d.id) && (!q.trim() || d.name.toLowerCase().includes(q.trim().toLowerCase())));
  const assign = async (d) => {
    try {
      await api.post("/admin/library-pro/assignments", { director_id: d.id, subcategory_id: sub.id });
      toast.success(`"${d.name}" assigned to ${sub.name}.`);
      onSaved();
      onClose();
    } catch (e) { toast.error(errMsg(e, "Couldn't assign.")); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0d0d0d] border-white/10 text-white max-w-md" data-testid="libadmin-assign-dialog">
        <DialogHeader><DialogTitle className="font-display text-lg">Assign a director → {sub.name}</DialogTitle></DialogHeader>
        <input data-testid="libadmin-assign-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search directors…" className={inputCls} autoFocus />
        <div className="max-h-72 overflow-y-auto space-y-1 mt-2">
          {candidates.map((d) => (
            <button key={d.id} data-testid={`libadmin-assign-${d.slug}`} onClick={() => assign(d)}
              className="w-full text-left px-3 py-2 rounded-lg hover:bg-white/10 text-sm flex items-center gap-2">
              <FilmSlate size={14} weight="fill" className="text-amber-300 shrink-0" />
              <span className="truncate">{d.name}</span>
              {d.library_only && <span className="ml-auto text-[9px] px-1.5 py-0.5 rounded bg-white/5 text-zinc-500 shrink-0">library</span>}
            </button>
          ))}
          {candidates.length === 0 && <p className="text-xs text-zinc-600 px-3 py-4">No unassigned directors match.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function LibraryAdmin() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [tree, setTree] = useState(null);
  const [inventory, setInventory] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [edit, setEdit] = useState(null);
  const [dirDialog, setDirDialog] = useState(null);
  const [assignSub, setAssignSub] = useState(null);

  const load = useCallback(() => {
    Promise.all([api.get("/admin/library-pro/tree"), api.get("/admin/library-pro/inventory")])
      .then(([t, i]) => { setTree(t.data); setInventory(i.data); })
      .catch(() => toast.error("Couldn't load the library admin."));
  }, []);
  useEffect(() => { if (user?.is_admin) load(); }, [user, load]);

  if (!authLoading && user && !user.is_admin) { navigate("/dashboard"); return null; }

  const act = async (fn, success) => {
    try { await fn(); if (success) toast.success(success); load(); }
    catch (e) { toast.error(errMsg(e, "Something went wrong.")); }
  };

  const moveCat = (idx, dir) => {
    const ids = tree.categories.map((c) => c.id);
    const j = idx + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    act(() => api.post("/admin/library-pro/categories/reorder", { ordered_ids: ids }));
  };

  const moveSub = (cat, idx, dir) => {
    const ids = cat.subcategories.map((s) => s.id);
    const j = idx + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    act(() => api.post("/admin/library-pro/subcategories/reorder", { ordered_ids: ids }));
  };

  return (
    <div data-testid="library-admin" className="min-h-screen bg-[#070707] text-white font-body">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/60 border-b border-white/10">
        <div className="max-w-[1200px] mx-auto px-6 h-16 flex items-center gap-3">
          <button onClick={() => navigate("/dashboard")} data-testid="libadmin-back-btn" className="h-9 w-9 rounded-lg hover:bg-white/10 flex items-center justify-center transition-colors"><ArrowLeft size={18} /></button>
          <span className="font-display font-extrabold text-lg tracking-tight">Official Library Manager</span>
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-500/15 text-red-300 ring-1 ring-red-400/30">ADMIN</span>
          <div className="ml-auto flex gap-2">
            <button data-testid="libadmin-new-director-btn" onClick={() => setDirDialog({ kind: "new-director" })} className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full bg-white/5 hover:bg-white/15 text-sm font-semibold transition-colors">
              <FilmSlate size={15} weight="fill" className="text-amber-300" /> New Director
            </button>
            <button data-testid="libadmin-new-category-btn" onClick={() => setEdit({ kind: "new-category" })} className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full bg-amber-400 text-black hover:bg-amber-300 text-sm font-bold transition-colors">
              <Plus size={15} weight="bold" /> New Category
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-6 py-8 space-y-8">
        {/* Inventory */}
        {inventory && (
          <div data-testid="libadmin-inventory" className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
            {[
              ["Categories", inventory.categories], ["Subcategories", inventory.subcategories],
              ["Directors", inventory.directors_total], ["Pre-existing", inventory.directors_pre_existing],
              ["Seeded", inventory.directors_seeded_by_library], ["Assigned", inventory.directors_with_assignments],
              ["Backed up", inventory.directors_backed_up], ["Open conflicts", inventory.conflicts_open],
            ].map(([label, n]) => (
              <div key={label} className="rounded-xl border border-white/10 bg-white/[0.03] p-3 text-center">
                <p className="font-display font-black text-xl">{n}</p>
                <p className="text-[10px] text-zinc-500 uppercase tracking-wider">{label}</p>
              </div>
            ))}
          </div>
        )}

        {/* Conflicts */}
        {tree?.conflicts?.length > 0 && (
          <section data-testid="libadmin-conflicts" className="rounded-2xl border border-amber-400/30 bg-amber-400/5 p-5">
            <p className="font-bold text-sm flex items-center gap-2 mb-3"><WarningCircle size={16} weight="fill" className="text-amber-300" /> Needs review ({tree.conflicts.length})</p>
            <div className="space-y-2">
              {tree.conflicts.map((c) => (
                <div key={c.id} className="flex items-start gap-3 text-[12px] text-zinc-300">
                  <span className="flex-1">{c.note}</span>
                  <button data-testid={`libadmin-resolve-${c.id}`} onClick={() => act(() => api.put(`/admin/library-pro/conflicts/${c.id}/resolve`), "Marked as reviewed.")}
                    className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/5 hover:bg-white/15 text-[11px] font-semibold transition-colors">
                    <Check size={12} weight="bold" /> Reviewed
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Tree */}
        {!tree ? <p className="text-sm text-zinc-500 py-16 text-center">Loading…</p> : (
          <section className="space-y-3">
            {tree.categories.map((c, ci) => (
              <div key={c.id} data-testid={`libadmin-cat-${c.id}`} className="rounded-2xl border border-white/10 bg-white/[0.02]">
                <div className="flex items-center gap-2 px-4 py-3">
                  <button onClick={() => setExpanded((e) => ({ ...e, [c.id]: !e[c.id] }))} className="text-zinc-500 hover:text-white">
                    {expanded[c.id] ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}
                  </button>
                  <span className="text-lg">{c.icon}</span>
                  <p className="font-bold text-[14px] truncate">{c.name}</p>
                  <span className="text-xs text-zinc-600">{c.subcategories.length} subs</span>
                  <div className="ml-auto flex items-center gap-1.5">
                    <StatusSelect testid={`libadmin-cat-status-${c.id}`} value={c.status} onChange={(s) => act(() => api.put(`/admin/library-pro/categories/${c.id}`, { status: s }))} />
                    <button onClick={() => moveCat(ci, -1)} className="h-7 w-7 rounded-full hover:bg-white/10 flex items-center justify-center text-zinc-500"><ArrowUp size={13} /></button>
                    <button onClick={() => moveCat(ci, 1)} className="h-7 w-7 rounded-full hover:bg-white/10 flex items-center justify-center text-zinc-500"><ArrowDown size={13} /></button>
                    <button data-testid={`libadmin-cat-edit-${c.id}`} onClick={() => setEdit({ kind: "edit-category", doc: c })} className="h-7 w-7 rounded-full hover:bg-white/10 flex items-center justify-center text-zinc-400"><PencilSimple size={13} /></button>
                    <button data-testid={`libadmin-cat-addsub-${c.id}`} onClick={() => setEdit({ kind: "new-subcategory", categoryId: c.id })} title="Add subcategory" className="h-7 w-7 rounded-full hover:bg-white/10 flex items-center justify-center text-amber-300"><Plus size={13} weight="bold" /></button>
                  </div>
                </div>
                {expanded[c.id] && (
                  <div className="border-t border-white/5 divide-y divide-white/5">
                    {c.subcategories.map((s, si) => (
                      <div key={s.id} data-testid={`libadmin-sub-${s.id}`} className="px-4 py-3 pl-10">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-[13px] truncate">{s.name}</p>
                          <div className="ml-auto flex items-center gap-1.5">
                            <StatusSelect testid={`libadmin-sub-status-${s.id}`} value={s.status} onChange={(st) => act(() => api.put(`/admin/library-pro/subcategories/${s.id}`, { status: st }))} />
                            <select
                              data-testid={`libadmin-sub-move-${s.id}`}
                              value={c.id}
                              onChange={(e) => act(() => api.put(`/admin/library-pro/subcategories/${s.id}`, { category_id: e.target.value }), "Subcategory moved.")}
                              className="text-[10px] rounded-full px-2 py-1 bg-white/5 text-zinc-400 border-0 outline-none cursor-pointer max-w-[120px]"
                              title="Move to another category"
                            >
                              {tree.categories.map((cc) => <option key={cc.id} value={cc.id} className="bg-[#161616]">{cc.name}</option>)}
                            </select>
                            <button onClick={() => moveSub(c, si, -1)} className="h-6 w-6 rounded-full hover:bg-white/10 flex items-center justify-center text-zinc-500"><ArrowUp size={12} /></button>
                            <button onClick={() => moveSub(c, si, 1)} className="h-6 w-6 rounded-full hover:bg-white/10 flex items-center justify-center text-zinc-500"><ArrowDown size={12} /></button>
                            <button data-testid={`libadmin-sub-edit-${s.id}`} onClick={() => setEdit({ kind: "edit-subcategory", doc: s })} className="h-6 w-6 rounded-full hover:bg-white/10 flex items-center justify-center text-zinc-400"><PencilSimple size={12} /></button>
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-1.5 mt-2">
                          {(s.directors || []).map((d) => (
                            <span key={d.assignment_id} data-testid={`libadmin-asg-${d.assignment_id}`} className="inline-flex items-center gap-1 pl-2 pr-1 py-1 rounded-full bg-white/5 ring-1 ring-white/10 text-[11px]">
                              <FilmSlate size={11} weight="fill" className="text-amber-300" />
                              <button onClick={() => setDirDialog({ kind: "edit-director", doc: d })} className="hover:text-amber-200">{d.name}</button>
                              {!d.library_only && <span className="text-[8px] px-1 rounded bg-blue-500/20 text-blue-300">original</span>}
                              <button onClick={() => setDirDialog(null) || act(() => api.post(`/admin/creative-directors/${d.id}/duplicate`), "Director duplicated.")} title="Duplicate director" className="h-4 w-4 rounded-full hover:bg-white/15 flex items-center justify-center text-zinc-500"><Copy size={9} /></button>
                              <button data-testid={`libadmin-unassign-${d.assignment_id}`} onClick={() => act(() => api.delete(`/admin/library-pro/assignments/${d.assignment_id}`), "Assignment removed.")} title="Remove from this subcategory (director itself is kept)" className="h-4 w-4 rounded-full hover:bg-red-500/30 flex items-center justify-center text-zinc-500 hover:text-red-300"><X size={9} weight="bold" /></button>
                            </span>
                          ))}
                          <button data-testid={`libadmin-assign-btn-${s.id}`} onClick={() => setAssignSub(s)} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-dashed border-white/20 text-[11px] text-zinc-400 hover:border-amber-400/50 hover:text-amber-300 transition-colors">
                            <LinkSimple size={11} weight="bold" /> Assign director
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </section>
        )}

        {/* Unassigned directors */}
        {tree?.unassigned_directors?.length > 0 && (
          <section data-testid="libadmin-unassigned" className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
            <p className="font-bold text-sm mb-1">Unassigned Creative Directors ({tree.unassigned_directors.length})</p>
            <p className="text-[11px] text-zinc-500 mb-3">These directors work everywhere they already work today — assign them to subcategories to also surface them in the Design Library.</p>
            <div className="flex flex-wrap gap-1.5">
              {tree.unassigned_directors.map((d) => (
                <span key={d.id} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-white/5 ring-1 ring-white/10 text-[12px]">
                  <FilmSlate size={12} weight="fill" className="text-amber-300" />
                  <button onClick={() => setDirDialog({ kind: "edit-director", doc: d })} className="hover:text-amber-200">{d.name}</button>
                  {!d.is_active && <span className="text-[8px] px-1 rounded bg-zinc-700 text-zinc-400">inactive</span>}
                </span>
              ))}
            </div>
          </section>
        )}
      </main>

      <EditDialog edit={edit} onClose={() => setEdit(null)} onSaved={load} />
      <DirectorDialog dialog={dirDialog} onClose={() => setDirDialog(null)} onSaved={load} />
      <AssignDialog sub={assignSub} allDirectors={tree?.all_directors || []} onClose={() => setAssignSub(null)} onSaved={load} />
    </div>
  );
}
